import React, { useEffect, useRef, useState } from "react";
import ThreeDotIcon from "../../SVG/three-dot-icon";
import "./three-dot-button.scss";

export interface ThreeDotOption {
  id: string;
  text: string;
}

interface ThreeDotButtonProps {
  options: ThreeDotOption[];
  onOptionClick: (optionId: string) => void;
  className?: string;
}

export function ThreeDotButton(props: ThreeDotButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [shouldFlipUp, setShouldFlipUp] = useState(false);
  const [shouldAlignLeft, setShouldAlignLeft] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Calculate position and determine if menu should flip up or align left
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const menuWidth = 160; // Approximate menu width (10rem)
      const menuHeight = 200; // Approximate max menu height
      const spaceBelow = window.innerHeight - buttonRect.bottom;
      const spaceRight = window.innerWidth - buttonRect.right;
      
      setShouldFlipUp(spaceBelow < menuHeight && buttonRect.top > menuHeight);
      setShouldAlignLeft(spaceRight < menuWidth && buttonRect.left > menuWidth);
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        buttonRef.current &&
        menuRef.current &&
        !buttonRef.current.contains(event.target as Node) &&
        !menuRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const handleOptionClick = (optionId: string) => {
    props.onOptionClick(optionId);
    setIsOpen(false);
  };

  return (
    <div className={`three-dot-button-container ${props.className ?? ""}`}>
      <div
        className="three-dot-button-trigger"
        onClick={handleButtonClick}
        ref={buttonRef}
      >
        <ThreeDotIcon />
      </div>
      {isOpen && (
        <div
          className={`three-dot-button-menu ${
            shouldFlipUp ? "position-top" : "position-bottom"
          } ${shouldAlignLeft ? "align-left" : "align-right"}`}
          ref={menuRef}
        >
          {props.options.map((option) => (
            <div
              key={option.id}
              className="three-dot-button-option"
              onClick={() => handleOptionClick(option.id)}
            >
              {option.text}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
